import React from 'react';
import { Box, Typography, CircularProgress, Grid, Card, CardMedia, CardContent } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { supabaseHelpers } from '../lib/supabase';

/**
 * Simple test component to verify Supabase product integration
 */
const ProductCatalogTest = () => {
  const {
    data: products,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["products-test"],
    queryFn: async () => {
      const { data, error } = await supabaseHelpers.getProducts();
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading products from Supabase...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">
          Error loading products: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Supabase Product Catalog Test
      </Typography>
      <Typography variant="body1" gutterBottom>
        Found {products?.length || 0} products in the database
      </Typography>
      
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {products?.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id}>
            <Card sx={{ height: '100%' }}>
              <CardMedia
                component="img"
                height="200"
                image={product.images?.[0]?.url || 'https://via.placeholder.com/400x300?text=No+Image'}
                alt={product.name}
                sx={{ objectFit: 'cover' }}
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {product.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {product.short_description}
                </Typography>
                <Typography variant="h6" color="primary">
                  ${product.price}
                </Typography>
                <Typography variant="caption" display="block">
                  Category: {product.category?.name || 'N/A'}
                </Typography>
                <Typography variant="caption" display="block">
                  Slug: {product.slug}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Debug info */}
      <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>Debug Info:</Typography>
        <pre style={{ fontSize: '12px', overflow: 'auto' }}>
          {JSON.stringify(products?.[0], null, 2)}
        </pre>
      </Box>
    </Box>
  );
};

export default ProductCatalogTest;